import React, { useState, useRef } from 'react';
import styled from 'styled-components';

import useAPI from '@hooks/useAPI';
import Button from '@shared/Button';
import Spinner from '@shared/Spinner';
import ErrorMessage from '@src/components/Input/ErrorMessage';

import PreviewDiffModal from './PreviewDiffModal';

const Container = styled.div`
  width: 70%;
  margin: auto;
`;

const UploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
`;

const FileName = styled.span`
  font-size: 14px;
  color: #555;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const HelpText = styled.p`
  font-size: 13px;
  color: #888;
  margin: 0 0 16px 0;
`;

export default function PreviewRuleDiff() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedRules, setParsedRules] = useState(null);
  const [parseError, setParseError] = useState('');
  const [previewParams, setPreviewParams] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);

  const previewResponse = useAPI(...previewParams);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    setParseError('');
    setParsedRules(null);
    setSelectedFile(file);

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const json = JSON.parse(e.target.result);
        // The endpoint expects a JSON array of rule objects
        if (!Array.isArray(json)) {
          setParseError(
            'Invalid format: the JSON file must contain an array of rule objects.'
          );
          return;
        }
        setParsedRules(json);
      } catch (err) {
        setParseError(`Failed to parse JSON: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handlePreview = () => {
    if (!parsedRules) return;

    setShowModal(true);
    setPreviewParams([
      'post',
      'ScreenConsoleApi',
      `config/${process.env.REACT_APP_CUSTOMER_ID}/rules/preview_diff`,
      Date.now().toString(),
      false,
      '',
      false,
      parsedRules,
    ]);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleClear = () => {
    setSelectedFile(null);
    setParsedRules(null);
    setParseError('');
    setPreviewParams([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Container>
      <HelpText>
        Upload a rules JSON file to preview what would change before applying.
      </HelpText>

      {parseError && <ErrorMessage error={parseError} />}

      <UploadRow>
        <Button onClick={handleFileSelect}>Choose rules file…</Button>
        <FileName>
          {selectedFile ? selectedFile.name : 'No file selected'}
        </FileName>
        <input
          type='file'
          accept='.json'
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </UploadRow>

      <UploadRow>
        <Button
          onClick={handlePreview}
          disabled={!parsedRules}
        >
          Preview Changes
        </Button>
        {selectedFile && (
          <Button
            onClick={handleClear}
            style={{
              background: 'transparent',
              color: '#888',
              border: '1px solid #ccc',
            }}
          >
            Clear
          </Button>
        )}
      </UploadRow>

      {showModal && (
        <PreviewDiffModal
          response={previewResponse.response}
          isLoading={previewResponse.isLoading}
          error={previewResponse.error}
          handleClose={handleCloseModal}
        />
      )}
    </Container>
  );
}
